import React, { useState, useEffect } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Cell, PieChart, Pie } from 'recharts';
import { FaFunnelDollar, FaSpinner, FaExclamationTriangle, FaComments, FaCheckCircle, FaCalendarCheck, FaHandshake, FaUserPlus, FaRocket, FaChartBar, FaEye, FaEyeSlash, FaDatabase } from 'react-icons/fa';
import useAuthenticatedAxios from "../../../auth/AuthenticatedAxios";
import styled from 'styled-components';

import { 
    COLORS, 
    PanelContent, 
    PanelTitle
} from './styles';

const FunnelContainer = styled.div`
    display: grid;
    grid-template-columns: auto 1fr 40px 50px;
    gap: 4px 8px;
    margin-bottom: 12px;
    @media screen and (max-width: 1920px) { gap: 3px 6px; }
`;

const FunnelRow = styled.div`
    display: contents;
    &:hover .step-visual {
        background: ${props => props.$hoverColor || 'rgba(99, 102, 241, 0.15)'};
        transform: translateX(2px);
    }
`;

const StepVisual = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 10px;
    background: rgba(25, 30, 45, 0.6);
    border-left: 3px solid ${props => props.$color || COLORS.primary};
    border-radius: 0 6px 6px 0;
    transition: all 0.2s ease;
    .icon {
        width: 24px; height: 24px;
        display: flex; align-items: center; justify-content: center;
        border-radius: 5px;
        background: ${props => props.$color || COLORS.primary}25;
        color: ${props => props.$color || COLORS.primary};
        font-size: 11px; flex-shrink: 0;
    }
    @media screen and (max-width: 1920px) {
        padding: 5px 8px;
        .icon { width: 22px; height: 22px; font-size: 10px; }
    }
`;

const StepLabel = styled.div`
    font-size: 12px; color: ${COLORS.light};
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    font-weight: 500; display: flex; align-items: center;
    @media screen and (max-width: 1920px) { font-size: 11px; }
`;

const StepValue = styled.div`
    font-size: 15px; font-weight: 700; color: ${COLORS.light};
    text-align: right; display: flex; align-items: center; justify-content: flex-end;
    @media screen and (max-width: 1920px) { font-size: 13px; }
`;

const StepRate = styled.div`
    font-size: 11px; color: ${props => props.$color || COLORS.primary};
    font-weight: 600; text-align: right;
    display: flex; align-items: center; justify-content: flex-end;
    @media screen and (max-width: 1920px) { font-size: 10px; }
`;

const TabBar = styled.div`
    display: flex;
    gap: 2px;
    margin-bottom: 10px;
    background: rgba(15, 18, 30, 0.5);
    border-radius: 6px;
    padding: 2px;
`;

const Tab = styled.button`
    flex: 1;
    padding: 5px 8px;
    border: none;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    background: ${props => props.$active ? 'rgba(99, 102, 241, 0.25)' : 'transparent'};
    color: ${props => props.$active ? COLORS.light : COLORS.muted};
    &:hover { background: ${props => props.$active ? 'rgba(99, 102, 241, 0.25)' : 'rgba(255,255,255,0.05)'}; }
`;

const StatCard = styled.div`
    background: rgba(25, 30, 45, 0.6);
    border-radius: 8px;
    padding: 10px 12px;
    border-left: 3px solid ${props => props.$color || COLORS.primary};
`;

const StatRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    &:last-child { border-bottom: none; }
`;

const EngagementReach = () => {
    const authenticatedAxios = useAuthenticatedAxios();
    const [data, setData] = useState(null);
    const [statsData, setStatsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('funnel');
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);
                const [funnelRes, statsRes] = await Promise.allSettled([
                    authenticatedAxios.get('dashboard/funnel').catch(() => 
                        authenticatedAxios.get('dashboard/user-journey')
                    ),
                    authenticatedAxios.get('dashboard/conversation-stats')
                ]);
                if (funnelRes.status === 'fulfilled') setData(funnelRes.value.data);
                if (statsRes.status === 'fulfilled') setStatsData(statsRes.value.data);
                if (funnelRes.status === 'rejected' && statsRes.status === 'rejected') {
                    setError('Failed to load data');
                }
            } catch (err) {
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        const interval = setInterval(fetchData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, [authenticatedAxios]);

    if (loading) {
        return (
            <PanelContent>
                <PanelTitle><FaFunnelDollar style={{ color: '#f59e0b' }} /> User Journey</PanelTitle>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.muted }}>
                    <FaSpinner style={{ marginRight: 8, animation: 'spin 1s linear infinite' }} /> Loading...
                </div>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </PanelContent>
        );
    }

    if (error && !data && !statsData) {
        return (
            <PanelContent>
                <PanelTitle><FaFunnelDollar style={{ color: '#f59e0b' }} /> User Journey</PanelTitle>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: COLORS.warning, textAlign: 'center' }}>
                    <FaExclamationTriangle style={{ fontSize: 24, marginBottom: 8 }} />
                    <div>{error}</div>
                </div>
            </PanelContent>
        );
    }

    const funnel = data?.totals || data?.funnel || {};
    const rates = data?.conversionRates || {};
    const weeklyTrend = data?.weeklyTrend || [
        { week: 'Week 1', opened: 0, started: 0, finished: 0, signups: 0 },
        { week: 'Week 2', opened: 0, started: 0, finished: 0, signups: 0 },
        { week: 'Week 3', opened: 0, started: 0, finished: 0, signups: 0 },
        { week: 'Week 4', opened: 0, started: 0, finished: 0, signups: 0 }
    ];

    const funnelSteps = [
        { key: 'widgetOpened', label: 'Widget Opened', value: funnel.widgetOpened ?? funnel.widgetConversations ?? 0, color: '#8b5cf6', icon: FaRocket, rate: null },
        { key: 'conversationStarted', label: 'Conversation Started', value: funnel.conversationStarted ?? funnel.activeConversations ?? 0, color: '#6366f1', icon: FaComments, rate: rates.openToStart ?? rates.widgetToStart },
        { key: 'conversationFinished', label: 'Conversation Finished', value: funnel.conversationFinished ?? funnel.finishedConversations ?? 0, color: '#3b82f6', icon: FaCheckCircle, rate: rates.startToFinish },
        { key: 'userSignedUp', label: 'User Signed Up', value: funnel.userSignedUp ?? 0, color: '#10b981', icon: FaUserPlus, rate: rates.finishToSignup },
        { key: 'meetingCreated', label: 'Meeting Booked', value: funnel.meetingCreated ?? 0, color: '#f59e0b', icon: FaCalendarCheck, rate: rates.signupToMeeting },
        { key: 'adviceConcluded', label: 'Advice Concluded', value: funnel.adviceConcluded ?? funnel.concludedAdvice ?? 0, color: '#059669', icon: FaHandshake, rate: rates.meetingToConcluded }
    ];

    const renderFunnelTab = () => (
        <>
            <FunnelContainer>
                {funnelSteps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                        <FunnelRow key={step.key} $color={step.color}>
                            <StepVisual className="step-visual" $color={step.color}>
                                <div className="icon"><Icon /></div>
                            </StepVisual>
                            <StepLabel>{step.label}</StepLabel>
                            <StepValue>{step.value}</StepValue>
                            <StepRate $color={step.color}>
                                {index > 0 ? `${step.rate ?? 0}%` : ''}
                            </StepRate>
                        </FunnelRow>
                    );
                })}
            </FunnelContainer>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ color: COLORS.muted, fontSize: 9, marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>4-Week Trend</div>
                <div style={{ flex: 1, minHeight: '100px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={weeklyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorOpened" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/><stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                </linearGradient>
                                <linearGradient id="colorStarted" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/><stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                </linearGradient>
                                <linearGradient id="colorFinished" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/><stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                            <XAxis dataKey="week" stroke={COLORS.muted} fontSize={8} tickLine={false} />
                            <YAxis stroke={COLORS.muted} fontSize={8} tickLine={false} axisLine={false} />
                            <RechartsTooltip
                                contentStyle={{ backgroundColor: COLORS.bgSecondary, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 10px' }}
                                itemStyle={{ color: COLORS.light, fontSize: 11 }}
                                labelStyle={{ color: COLORS.light, fontWeight: 600, fontSize: 11 }}
                            />
                            <Area type="monotone" dataKey="opened" stroke="#8b5cf6" fillOpacity={1} fill="url(#colorOpened)" strokeWidth={2} name="Opened" />
                            <Area type="monotone" dataKey="started" stroke="#6366f1" fillOpacity={1} fill="url(#colorStarted)" strokeWidth={2} name="Started" />
                            <Area type="monotone" dataKey="finished" stroke="#10b981" fillOpacity={1} fill="url(#colorFinished)" strokeWidth={2} name="Finished" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </>
    );

    const renderStatsTab = () => {
        if (!statsData) {
            return <div style={{ color: COLORS.muted, textAlign: 'center', padding: 20, fontSize: 12 }}>Statistics unavailable</div>;
        }

        const d = statsData.distribution;
        const v = statsData.visibility;
        const total = statsData.total;
        const real = statsData.realConversations;

        const distributionData = [
            { label: 'Empty (0 msgs)', value: d.zero, color: '#6b7280', desc: 'Widget opened, no interaction' },
            { label: 'Minimal (1-3)', value: d.oneToThree, color: '#8b5cf6', desc: 'Quick drop-off' },
            { label: 'Short (4-10)', value: d.fourToTen, color: '#f59e0b', desc: 'Abandoned during identification' },
            { label: 'Medium (11-20)', value: d.elevenToTwenty, color: '#3b82f6', desc: 'Partial conversations' },
            { label: 'Full (20+)', value: d.twentyPlus, color: '#10b981', desc: 'Real, substantial chats' },
        ];

        const visibilityData = [
            { label: 'In Leads', value: v.inLeads, color: '#10b981', icon: FaCheckCircle },
            { label: 'In Incomplete', value: v.inIncompleteVisible ?? v.inIncomplete, color: '#f59e0b', icon: FaEye, subtitle: v.inIncompleteHidden ? `+${v.inIncompleteHidden} hidden` : null },
            { label: 'Not Visible', value: v.invisible, color: '#ef4444', icon: FaEyeSlash },
        ];

        const barData = distributionData.map(item => ({
            name: item.label,
            value: item.value,
            fill: item.color
        }));

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, overflow: 'auto', flex: 1 }}>
                {/* Top summary */}
                <div style={{ display: 'flex', gap: 6 }}>
                    <div style={{ flex: 1, background: 'rgba(25,30,45,0.6)', borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.light }}>{total}</div>
                        <div style={{ fontSize: 9, color: COLORS.muted, textTransform: 'uppercase' }}>Total</div>
                    </div>
                    <div style={{ flex: 1, background: 'rgba(25,30,45,0.6)', borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#10b981' }}>{real}</div>
                        <div style={{ fontSize: 9, color: COLORS.muted, textTransform: 'uppercase' }}>Real Convos</div>
                    </div>
                    <div style={{ flex: 1, background: 'rgba(25,30,45,0.6)', borderRadius: 6, padding: '8px 10px', textAlign: 'center' }}>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{total > 0 ? Math.round((real / total) * 100) : 0}%</div>
                        <div style={{ fontSize: 9, color: COLORS.muted, textTransform: 'uppercase' }}>Engagement</div>
                    </div>
                </div>

                {/* Message distribution bar chart */}
                <StatCard $color="#6366f1">
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        <FaChartBar style={{ marginRight: 4 }} /> Message Distribution
                    </div>
                    <div style={{ height: 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={barData} margin={{ top: 2, right: 0, left: -20, bottom: 0 }}>
                                <XAxis dataKey="name" stroke={COLORS.muted} fontSize={7} tickLine={false} interval={0} angle={-15} textAnchor="end" height={30} />
                                <YAxis stroke={COLORS.muted} fontSize={8} tickLine={false} axisLine={false} />
                                <RechartsTooltip
                                    contentStyle={{ backgroundColor: COLORS.bgSecondary, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: '6px 10px' }}
                                    itemStyle={{ color: COLORS.light, fontSize: 11 }}
                                    labelStyle={{ color: COLORS.light, fontWeight: 600, fontSize: 11 }}
                                />
                                <Bar dataKey="value" radius={[3, 3, 0, 0]} name="Conversations">
                                    {barData.map((entry, index) => (
                                        <Cell key={index} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </StatCard>

                {/* Visibility status */}
                <StatCard $color="#3b82f6">
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        <FaDatabase style={{ marginRight: 4 }} /> Where Are Your Conversations?
                    </div>
                    {visibilityData.map((item) => {
                        const Icon = item.icon;
                        const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
                        return (
                            <StatRow key={item.label}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <Icon style={{ color: item.color, fontSize: 10 }} />
                                    <div>
                                        <span style={{ fontSize: 11, color: COLORS.light }}>{item.label}</span>
                                        {item.subtitle && (
                                            <div style={{ fontSize: 9, color: COLORS.muted, marginTop: 1 }}>{item.subtitle}</div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.light }}>{item.value}</span>
                                    <span style={{ fontSize: 9, color: item.color, fontWeight: 600 }}>{pct}%</span>
                                </div>
                            </StatRow>
                        );
                    })}
                    {v.invisible > 0 && (
                        <div style={{ fontSize: 9, color: '#ef4444', marginTop: 6, padding: '4px 6px', background: 'rgba(239,68,68,0.1)', borderRadius: 4 }}>
                            {v.invisible} conversations are not visible in any list (pre-funnel tracking or no funnel session)
                        </div>
                    )}
                </StatCard>

                {/* Detailed breakdown */}
                <StatCard $color="#8b5cf6">
                    <div style={{ fontSize: 10, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
                        Detailed Breakdown
                    </div>
                    {distributionData.map((item) => (
                        <StatRow key={item.label}>
                            <div>
                                <div style={{ fontSize: 11, color: COLORS.light }}>{item.label}</div>
                                <div style={{ fontSize: 8, color: COLORS.muted }}>{item.desc}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                                    <div style={{ width: `${total > 0 ? (item.value / total) * 100 : 0}%`, height: '100%', background: item.color, borderRadius: 2 }} />
                                </div>
                                <span style={{ fontSize: 12, fontWeight: 700, color: item.color, minWidth: 30, textAlign: 'right' }}>{item.value}</span>
                            </div>
                        </StatRow>
                    ))}
                </StatCard>
            </div>
        );
    };

    return (
        <PanelContent>
            <TabBar>
                <Tab $active={activeTab === 'funnel'} onClick={() => setActiveTab('funnel')}>
                    <FaFunnelDollar /> Funnel
                </Tab>
                <Tab $active={activeTab === 'stats'} onClick={() => setActiveTab('stats')}>
                    <FaChartBar /> Statistics
                </Tab>
            </TabBar>
            {activeTab === 'funnel' ? renderFunnelTab() : renderStatsTab()}
        </PanelContent>
    );
};

export default EngagementReach;
